import { InventoryItem } from "@1games-ru/region-shared/dist/types/inventory/inventory";
import { useDraggable } from "@dnd-kit/core";
import { useCallback, useMemo } from "react";
import clsx from "clsx";
import { SoundButton } from "@/shared/ui";
import { ItemRarity } from "@/domains/inventory/constants/itemRarity";
import { IS_DEV } from "@/domains/main/constants/other";
import accentColorByRarity from "@/domains/inventory/helpers/accentColorByRarity";
import InventoryBorderBox from "@/domains/inventory/components/InventoryBorderBox";

type Props = {
  inventoryId?: number;
  onClick?: (fromInventoryId: number, item: InventoryItem) => void | Promise<void>;
  onRefresh?: () => Promise<void> | void;
  item: InventoryItem;
};

export default function InventoryListItem({ inventoryId, onClick, onRefresh, item }: Props) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `listItem-${item.id}-${inventoryId ? `inv${inventoryId}` : ""}`,
    data: item,
  });

  const accentColor = useMemo(
    () => accentColorByRarity[item.itemTemplate.rarity] || accentColorByRarity[ItemRarity.Common],
    [item.itemTemplate.rarity],
  );

  // const width = useMemo(() => `calc(4.375rem * ${item.size.width} - 1px)`, [item.size.width]);
  // const height = useMemo(() => `calc(4.375rem * ${item.size.height} - 1px)`, [item.size.height]);

  const handleClick = useCallback(async () => {
    await onClick?.(inventoryId, item);
    await onRefresh?.();
  }, [inventoryId, item, onRefresh]);

  return (
    <div
      ref={setNodeRef}
      className={clsx(
        "w-full h-[5rem] p-1 flex items-center gap-2.5 shrink-0 bg-dark-900",
        "group border border-[#1A1A1A] rounded-[0.75rem] transition-all",
        "hover:z-1 hover:scale-105 hover:bg-[#1A1A1A]",
        "touch-manipulation", // это очень важно
        {
          "opacity-0": isDragging,
        },
      )}
      // style={
      //   {
      //     "--width": width,
      //     "--height": height,
      //   } as CSSProperties
      // }
      onDoubleClick={handleClick}
      {...attributes}
      {...listeners}
    >
      <InventoryBorderBox
        color={accentColor}
        className="w-[4.375rem] h-[4.375rem] bg-dark-800 hover:brightness-110 cursor-pointer rounded-[0.55rem]"
      >
        <div className="relative w-full h-full flex flex-col gap-1 items-center justify-center">
          <div
            className={clsx("w-full h-full flex items-center justify-center", {
              "-rotate-90 ": item.isRotated,
            })}
            style={{
              // ...(item.isRotated
              //   ? { width: "calc(0.8 * var(--height))", height: "calc(0.8 * var(--width))" }
              //   : { width: "calc(0.8 * var(--width))", height: "calc(0.8 * var(--height))" }),
              width: "80%",
              height: "80%",
              backgroundSize: "contain",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              backgroundImage: `url(https://cdn.region.game/images/inventory/items/${item.itemId}.webp)`,
            }}
          />
        </div>

        {IS_DEV && (
          <div className="absolute bottom-1 left-1 text-10 whitespace-nowrap">
            {item.itemTemplate.name}
          </div>
        )}

        {!!item.wear && (
          <div
            className={clsx(
              "absolute top-1 left-1 leading-[0.75] h-5 px-1.5 flex items-center text-11 font-bold text-[#84FF97] rounded-[0.3125rem]",
              "bg-gradient-to-r from-[#84FF97]/10 to-[#84FF97]/5",
            )}
          >
            {Number((item.wear * 100).toFixed(2))}%
          </div>
        )}

        {!!item.quantity && (item.itemTemplate.isStackable || item.itemTemplate.maxStack > 1) && (
          <div
            className="absolute flex items-center justify-center px-1 h-4 min-w-4 rounded-[0.3125rem] text-11 font-bold bottom-1 right-1"
            style={{ background: `${accentColor}33` }}
          >
            {item.quantity}
          </div>
        )}
      </InventoryBorderBox>

      <div className="flex flex-col items-start">
        <div className="text-15 font-medium">{item.itemTemplate.name}</div>
        <div className="text-14 font-medium text-white/50 overflow-hidden text-ellipsis whitespace-nowrap max-w-[20rem]">
          {item.itemTemplate.description}
        </div>
        <div className="text-11 font-bold mt-1">
          {Number(item.weight.toFixed(2))} <span className="text-10">KG</span>
        </div>
      </div>

      <SoundButton
        color="gray"
        variant="soft"
        className="ml-auto font-arial w-[2.25rem] h-full"
        onClick={handleClick}
      >
        ►
      </SoundButton>
    </div>
  );
}
